import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { getImageURL } from '../../utils/api';

// Local helper: map staff type to Tailwind classes (copied from Staff page)
const getStaffTypeColor = (type) => {
  const t = String(type || '').toLowerCase();
  if (!t) return 'bg-gray-100 text-gray-700 border-gray-200';
  if (t.includes('admin')) return 'bg-red-50 text-red-700 border-red-200';
  if (t.includes('coordinator') || t.includes('coord')) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  if (t.includes('lecturer') || t.includes('academic')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return 'bg-slate-50 text-slate-700 border-slate-200';
};

export default function CourseMapping() {
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [totalStaff, setTotalStaff] = useState(0);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [currentRoleFilter, setCurrentRoleFilter] = useState('all');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [imageErrors, setImageErrors] = useState({});
  const [isNewAssignment, setIsNewAssignment] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [removingCourseId, setRemovingCourseId] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleFormMode, setRoleFormMode] = useState('create');
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleFormData, setRoleFormData] = useState({
    role_name: '',
    description: '',
    role_type: '',
    category: '',
    priority: 50,
    is_active: true,
  });
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [staffForRoleChange, setStaffForRoleChange] = useState(null);
  const [selectedNewRole, setSelectedNewRole] = useState('');
  const [showCoursesListModal, setShowCoursesListModal] = useState(false);
  const [staffForCoursesList, setStaffForCoursesList] = useState(null);

  // Initial load
  useEffect(() => {
    const load = async (role = 'all') => {
      try {
        setLoading(true);
        const resp = await axiosInstance.get('/admin/course-mappings', {
          params: { role },
        });
        const data = resp?.data || {};
        setStaffList(data.staffList || []);
        setTotalStaff(data.totalStaff || 0);
        setAvailableRoles(data.availableRoles || []);
        setAllCourses(data.allCourses || []);
        setCurrentRoleFilter(data.currentRoleFilter || role || 'all');
      } catch (err) {
        console.error('Failed to load course mappings', err);
        Swal.fire('Error', err?.response?.data?.message || 'Failed to load course mappings', 'error');
      } finally {
        setLoading(false);
      }
    };
    load('all');
  }, []);

  const filteredStaff = staffList.filter((member) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      (member.name || '').toLowerCase().includes(term) ||
      (member.employeeNumber || '').toLowerCase().includes(term) ||
      (member.type || '').toLowerCase().includes(term) ||
      (member.position || '').toLowerCase().includes(term)
    );
  });

  const handleImageError = (staffId) => {
    setImageErrors((prev) => ({ ...prev, [staffId]: true }));
  };

  const getStaffImageUrl = (staff) => {
    if (!staff?.imagePath || imageErrors[staff.staffId]) return null;
    return getImageURL(staff.imagePath);
  };

  const handleRoleFilterChange = async (e) => {
    const newRole = e.target.value;
    setCurrentRoleFilter(newRole);
    try {
      setLoading(true);
      const resp = await axiosInstance.get('/admin/course-mappings', {
        params: { role: newRole },
      });
      const data = resp?.data || {};
      setStaffList(data.staffList || []);
      setTotalStaff(data.totalStaff || 0);
      setAvailableRoles(data.availableRoles || []);
      setAllCourses(data.allCourses || []);
      setCurrentRoleFilter(data.currentRoleFilter || newRole || 'all');
    } catch (err) {
      console.error('Failed to filter by role', err);
      Swal.fire('Error', err?.response?.data?.message || 'Failed to filter staff by role', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCourses = (staff) => {
    if (staff == null) {
      setIsNewAssignment(true);
      setSelectedStaff(null);
      setSelectedCourses([]);
    } else {
      setIsNewAssignment(false);
      setSelectedStaff(staff);
      const existingCourses = (staff.assignedCourses || []).map((course) => {
        const fullCourse = allCourses.find((c) => c.value === course.courseId);
        return {
          ...(fullCourse || {
            value: course.courseId,
            label: course.courseName || '',
            shortName: course.courseShortName || '',
          }),
          isView: course.isView ?? true,
          isAdd: course.isAdd ?? true,
          isUpdate: course.isUpdate ?? true,
          isDelete: course.isDelete ?? false,
        };
      });
      setSelectedCourses(existingCourses);
    }
    setShowAssignModal(true);
  };

  const handleSaveCourseMapping = async () => {
    if (!selectedStaff && !isNewAssignment) return;

    if (!selectedStaff && isNewAssignment) {
      Swal.fire({
        icon: 'warning',
        title: 'No Staff Selected',
        text: 'Please select a staff member to assign courses to.',
      });
      return;
    }

    if (selectedCourses.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Courses Selected',
        text: 'Please select at least one course to assign.',
      });
      return;
    }

    const coursesData = selectedCourses.map((course) => ({
      course_id: course.value,
      isView: course.isView,
      isAdd: course.isAdd,
      isUpdate: course.isUpdate,
      isDelete: course.isDelete,
    }));

    try {
      setIsProcessing(true);
      if (isNewAssignment && selectedStaff) {
        const resp = await axiosInstance.post('/admin/course-mappings', {
          staff_id: selectedStaff.staffId,
          courses: coursesData,
        });
        const data = resp?.data || {};
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: data.message || 'Courses assigned successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      } else if (selectedStaff) {
        const resp = await axiosInstance.put(`/admin/course-mappings/${selectedStaff.staffId}`, {
          courses: coursesData,
        });
        const data = resp?.data || {};
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: data.message || 'Course mappings updated successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      }

      // Reload staff list
      const reloadResp = await axiosInstance.get('/admin/course-mappings', {
        params: { role: currentRoleFilter },
      });
      const reloadData = reloadResp?.data || {};
      setStaffList(reloadData.staffList || []);
      setTotalStaff(reloadData.totalStaff || 0);
      setAvailableRoles(reloadData.availableRoles || []);
      setAllCourses(reloadData.allCourses || []);

      setShowAssignModal(false);
      setSelectedStaff(null);
      setSelectedCourses([]);
      setIsNewAssignment(false);
    } catch (err) {
      console.error('Course mapping save error', err);
      const errorMessage =
        err?.response?.data?.message ||
        (Array.isArray(err?.response?.data?.errors) ? err.response.data.errors.join('\n') : 'Failed to save course mappings');
      Swal.fire({ icon: 'error', title: 'Error', text: errorMessage });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveCourse = (staff, mappingId, courseName) => {
    Swal.fire({
      title: 'Remove Course?',
      text: `Are you sure you want to remove "${courseName}" from ${staff.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, remove it!',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await axiosInstance.delete(`/admin/course-mappings/mapping/${mappingId}`);
        Swal.fire({
          icon: 'success',
          title: 'Removed!',
          text: 'Course has been removed.',
          timer: 2000,
          showConfirmButton: false,
        });
        const reloadResp = await axiosInstance.get('/admin/course-mappings', {
          params: { role: currentRoleFilter },
        });
        const reloadData = reloadResp?.data || {};
        setStaffList(reloadData.staffList || []);
        setTotalStaff(reloadData.totalStaff || 0);
        setAvailableRoles(reloadData.availableRoles || []);
        setAllCourses(reloadData.allCourses || []);
      } catch (err) {
        console.error('Course removal error', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err?.response?.data?.message || 'Failed to remove course',
        });
      }
    });
  };

  const handleRemoveAllCourses = (staff) => {
    Swal.fire({
      title: 'Remove All Courses?',
      text: `Are you sure you want to remove all ${staff.assignedCoursesCount} course(s) from ${staff.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, remove all!',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await axiosInstance.delete(`/admin/course-mappings/staff/${staff.staffId}`);
        Swal.fire({
          icon: 'success',
          title: 'Removed!',
          text: 'All courses have been removed.',
          timer: 2000,
          showConfirmButton: false,
        });
        const reloadResp = await axiosInstance.get('/admin/course-mappings', {
          params: { role: currentRoleFilter },
        });
        const reloadData = reloadResp?.data || {};
        setStaffList(reloadData.staffList || []);
        setTotalStaff(reloadData.totalStaff || 0);
        setAvailableRoles(reloadData.availableRoles || []);
        setAllCourses(reloadData.allCourses || []);
      } catch (err) {
        console.error('Course removal error', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err?.response?.data?.message || 'Failed to remove courses',
        });
      }
    });
  };

  const handleManageRoles = () => {
    setShowRoleModal(true);
    setRoleFormMode('create');
    setSelectedRole(null);
    setRoleFormData({
      role_name: '',
      description: '',
      role_type: '',
      category: '',
      priority: 50,
      is_active: true,
    });
  };

  const handleEditRole = (role) => {
    setRoleFormMode('edit');
    setSelectedRole(role);
    setRoleFormData({
      role_name: role.label,
      description: role.description || '',
      role_type: role.value,
      category: role.category || '',
      priority: role.priority || 50,
      is_active: role.is_active,
    });
  };

  const handleSaveRole = async () => {
    if (!roleFormData.role_name.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Role name is required.',
      });
      return;
    }

    try {
      if (roleFormMode === 'create') {
        const resp = await axiosInstance.post('/admin/course-mappings/roles', roleFormData);
        const data = resp?.data || {};
        setAvailableRoles(data.availableRoles || []);
        setShowRoleModal(false);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: data.message || 'Role created successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      } else if (selectedRole) {
        const resp = await axiosInstance.put(`/admin/course-mappings/roles/${selectedRole.id}`, roleFormData);
        const data = resp?.data || {};
        setAvailableRoles(data.availableRoles || []);
        setShowRoleModal(false);
        setSelectedRole(null);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: data.message || 'Role updated successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error('Role save error', err);
      const errs = err?.response?.data?.errors;
      const msg =
        err?.response?.data?.message ||
        (Array.isArray(errs) && errs.length ? errs.join('\n') : 'Failed to save role');
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    }
  };

  const handleDeleteRole = (role) => {
    Swal.fire({
      title: 'Delete Role?',
      text: `Are you sure you want to delete the role "${role.label}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const resp = await axiosInstance.delete(`/admin/course-mappings/roles/${role.id}`);
        const data = resp?.data || {};
        setAvailableRoles(data.availableRoles || []);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: data.message || 'Role has been deleted.',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error('Role deletion error', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err?.response?.data?.message || 'Failed to delete role',
        });
      }
    });
  };

  const handleChangeStaffRole = (staff) => {
    setStaffForRoleChange(staff);
    setSelectedNewRole(staff.originalType || staff.type || '');
    setShowChangeRoleModal(true);
  };

  const handleSaveStaffRoleChange = async () => {
    if (!staffForRoleChange) return;
    if (!selectedNewRole) {
      Swal.fire({
        icon: 'warning',
        title: 'No Role Selected',
        text: 'Please select a role to assign.',
      });
      return;
    }

    try {
      const resp = await axiosInstance.put(`/admin/course-mappings/staff/${staffForRoleChange.staffId}/role`, {
        role_slug: selectedNewRole,
      });
      const data = resp?.data || {};
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: data.message || 'Staff role updated successfully',
        timer: 2000,
        showConfirmButton: false,
      });
      const reloadResp = await axiosInstance.get('/admin/course-mappings', {
        params: { role: currentRoleFilter },
      });
      const reloadData = reloadResp?.data || {};
      setStaffList(reloadData.staffList || []);
      setTotalStaff(reloadData.totalStaff || 0);
      setAvailableRoles(reloadData.availableRoles || []);
      setAllCourses(reloadData.allCourses || []);

      setShowChangeRoleModal(false);
      setStaffForRoleChange(null);
      setSelectedNewRole('');
    } catch (err) {
      console.error('Staff role update error', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err?.response?.data?.message || 'Failed to update staff role',
      });
    }
  };

  const handleViewCoursesList = (staff) => {
    setStaffForCoursesList(staff);
    setShowCoursesListModal(true);
  };

  const handleRemoveCourseFromList = (staff, mappingId, courseName) => {
    Swal.fire({
      title: 'Remove Course?',
      text: `Are you sure you want to remove "${courseName}" from ${staff.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, remove it!',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        setRemovingCourseId(mappingId);
        await axiosInstance.delete(`/admin/course-mappings/mapping/${mappingId}`);
        setRemovingCourseId(null);
        setStaffForCoursesList((prevStaff) => {
          if (!prevStaff) return null;
          return {
            ...prevStaff,
            assignedCourses: prevStaff.assignedCourses.filter((c) => c.id !== mappingId),
            assignedCoursesCount: prevStaff.assignedCoursesCount - 1,
          };
        });
        // Also update main staff list so the table reflects new counts
        setStaffList((prevList) =>
          prevList.map((item) => {
            if (item.staffId !== staff.staffId) return item;
            const updatedAssigned = (item.assignedCourses || []).filter((c) => c.id !== mappingId);
            const newCount = Math.max(0, (item.assignedCoursesCount || 0) - 1);
            return {
              ...item,
              assignedCourses: updatedAssigned,
              assignedCoursesCount: newCount,
            };
          }),
        );
        Swal.fire({
          icon: 'success',
          title: 'Removed!',
          text: 'Course has been removed.',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (err) {
        setRemovingCourseId(null);
        console.error('Course removal error', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err?.response?.data?.message || 'Failed to remove course',
        });
      }
    });
  };

  return (
    <DashboardLayout title="Course Mapping">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Course Mapping Management</h1>
          <p className="text-gray-600 mt-2">Assign and manage courses for staff members by role</p>
        </div>

        {/* Stats Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Staff Overview</h2>
              <p className="text-sm text-gray-600">
                Total staff members: <span className="font-semibold text-blue-600">{totalStaff}</span>
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Role Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Role</label>
              <select
                value={currentRoleFilter}
                onChange={handleRoleFilterChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Roles</option>
                {availableRoles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Bar */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Staff</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, employee number, role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg
                  className="absolute left-3 top-3 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Manage Roles Button */}
            <div className="flex items-end">
              <button
                onClick={handleManageRoles}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 flex items-center justify-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Manage Roles
              </button>
            </div>

            {/* Add Staff Button */}
            <div className="flex items-end">
              <button
                onClick={() => handleAssignCourses(null)}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex items-center justify-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add New Assignment
              </button>
            </div>
          </div>
        </div>

        {/* Staff List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Staff Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned Courses
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-8 text-center text-gray-500 text-sm"
                    >
                      Loading course mappings...
                    </td>
                  </tr>
                ) : filteredStaff.length > 0 ? (
                  filteredStaff.map((staff) => (
                    <tr key={staff.staffId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {getStaffImageUrl(staff) ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={getStaffImageUrl(staff)}
                                alt={staff.name}
                                onError={() => handleImageError(staff.staffId)}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-600 font-medium text-sm">
                                  {staff.name
                                    .split(' ')
                                    .map((n) => n[0])
                                    .join('')
                                    .toUpperCase()
                                    .slice(0, 2)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{staff.name}</div>
                            <div className="text-sm text-gray-500">
                              {staff.employeeNumber || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStaffTypeColor(
                            staff.type,
                          )}`}
                        >
                          {staff.type || 'No Role'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-md">
                          {staff.assignedCoursesCount > 0 ? (
                            <button
                              onClick={() => handleViewCoursesList(staff)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                                />
                              </svg>
                              <span>
                                {staff.assignedCoursesCount}{' '}
                                {staff.assignedCoursesCount === 1
                                  ? 'Course'
                                  : 'Courses'}
                              </span>
                            </button>
                          ) : staff.hasFullCourseAccess ? (
                            <span
                              className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold rounded-full"
                              title="Granted automatically by the backend for this role"
                            >
                              ✓ All courses · full access
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                              No courses assigned
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleChangeStaffRole(staff)}
                            disabled={staff.isSuperAdmin}
                            className="text-purple-600 hover:text-purple-900 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-purple-600"
                            title={staff.isSuperAdmin ? "Admin accounts can't be changed here" : "Change Role"}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleAssignCourses(staff)}
                            disabled={staff.isSuperAdmin}
                            className="text-blue-600 hover:text-blue-900 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-blue-600"
                            title={staff.isSuperAdmin ? "Admin accounts can't be changed here" : "Assign Courses"}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          {staff.assignedCoursesCount > 0 && (
                            <button
                              onClick={() => handleRemoveAllCourses(staff)}
                              disabled={staff.isSuperAdmin}
                              className="text-red-600 hover:text-red-900 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-red-600"
                              title={staff.isSuperAdmin ? "Admin accounts can't be changed here" : "Remove All Courses"}
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      No staff members found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Assign Courses Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {isNewAssignment
                  ? 'Add New Course Assignment'
                  : `Assign Courses to ${selectedStaff?.name}`}
              </h3>

              {isNewAssignment && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Staff Member
                  </label>
                  <Select
                    value={
                      selectedStaff
                        ? { value: selectedStaff.staffId, label: selectedStaff.name }
                        : null
                    }
                    onChange={(selected) => {
                      const staff = staffList.find(
                        (s) => s.staffId === (selected ? selected.value : null),
                      );
                      setSelectedStaff(staff || null);
                      if (staff && (staff.assignedCourses || []).length > 0) {
                        const existingCourses = staff.assignedCourses.map((course) => {
                          const fullCourse = allCourses.find(
                            (c) => c.value === course.courseId,
                          );
                          return {
                            ...(fullCourse || {
                              value: course.courseId,
                              label: course.courseName || '',
                              shortName: course.courseShortName || '',
                            }),
                            isView: course.isView ?? true,
                            isAdd: course.isAdd ?? true,
                            isUpdate: course.isUpdate ?? true,
                            isDelete: course.isDelete ?? false,
                          };
                        });
                        setSelectedCourses(existingCourses);
                      } else {
                        setSelectedCourses([]);
                      }
                    }}
                    options={staffList.map((staff) => ({
                      value: staff.staffId,
                      label: `${staff.name} (${staff.type || 'No Role'}) ${
                        staff.assignedCoursesCount > 0
                          ? `- ${staff.assignedCoursesCount} courses`
                          : ''
                      }`,
                    }))}
                    className="basic-select"
                    classNamePrefix="select"
                    placeholder="Select staff member..."
                    isClearable
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '42px',
                      }),
                    }}
                  />
                  {selectedStaff && selectedStaff.assignedCoursesCount > 0 && (
                    <p className="mt-2 text-sm text-amber-600">
                      ⚠️ This staff member already has {selectedStaff.assignedCoursesCount}{' '}
                      course(s) assigned. Their existing assignments are pre-loaded
                      below.
                    </p>
                  )}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Courses
                </label>
                <Select
                  isMulti
                  value={selectedCourses.map((c) => ({
                    value: c.value,
                    label: c.label,
                    shortName: c.shortName,
                  }))}
                  onChange={(selected) => {
                    const selectedArray = Array.isArray(selected) ? selected : [];
                    const newCourses = selectedArray.map((course) => {
                      const existing = selectedCourses.find(
                        (c) => c.value === course.value,
                      );
                      return (
                        existing || {
                          ...course,
                          isView: true,
                          isAdd: true,
                          isUpdate: true,
                          isDelete: false,
                        }
                      );
                    });
                    setSelectedCourses(newCourses);
                  }}
                  options={allCourses}
                  formatOptionLabel={(course) => (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{course.label}</div>
                        <div className="text-xs text-gray-500">{course.shortName}</div>
                      </div>
                      {course.scheduleType && (
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded ${
                            course.scheduleType === 'weekday'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}
                        >
                          {course.scheduleType === 'weekday' ? 'Weekday' : 'Weekend'}
                        </span>
                      )}
                    </div>
                  )}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select courses..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                    }),
                  }}
                />
                <p className="mt-2 text-sm text-gray-500">
                  Selected: {selectedCourses.length} course(s)
                </p>
              </div>

              {selectedCourses.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Course Permissions
                  </label>
                  <div className="space-y-3 max-h-80 overflow-y-auto border border-gray-300 rounded-lg p-4">
                    {selectedCourses.map((course, index) => (
                      <div
                        key={course.value}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 text-sm">
                              {course.label}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {course.shortName}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedCourses(
                                selectedCourses.filter((c) => c.value !== course.value),
                              );
                            }}
                            className="text-red-500 hover:text-red-700"
                            title="Remove course"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={course.isView}
                              onChange={(e) => {
                                const updated = [...selectedCourses];
                                updated[index].isView = e.target.checked;
                                setSelectedCourses(updated);
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">View</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={course.isAdd}
                              onChange={(e) => {
                                const updated = [...selectedCourses];
                                updated[index].isAdd = e.target.checked;
                                setSelectedCourses(updated);
                              }}
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <span className="text-sm text-gray-700">Add</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={course.isUpdate}
                              onChange={(e) => {
                                const updated = [...selectedCourses];
                                updated[index].isUpdate = e.target.checked;
                                setSelectedCourses(updated);
                              }}
                              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700">Update</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={course.isDelete}
                              onChange={(e) => {
                                const updated = [...selectedCourses];
                                updated[index].isDelete = e.target.checked;
                                setSelectedCourses(updated);
                              }}
                              className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                            />
                            <span className="text-sm text-gray-700">Delete</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-xs font-medium text-blue-800 mb-2">
                      Apply to all courses:
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => {
                          setSelectedCourses(
                            selectedCourses.map((c) => ({ ...c, isView: true })),
                          );
                        }}
                        className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Enable View All
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCourses(
                            selectedCourses.map((c) => ({ ...c, isAdd: true })),
                          );
                        }}
                        className="text-xs px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700"
                      >
                        Enable Add All
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCourses(
                            selectedCourses.map((c) => ({ ...c, isUpdate: true })),
                          );
                        }}
                        className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Enable Update All
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCourses(
                            selectedCourses.map((c) => ({ ...c, isDelete: true })),
                          );
                        }}
                        className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Enable Delete All
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCourses(
                            selectedCourses.map((c) => ({
                              ...c,
                              isView: false,
                              isAdd: false,
                              isUpdate: false,
                              isDelete: false,
                            })),
                          );
                        }}
                        className="text-xs px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                      >
                        Disable All
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedStaff(null);
                    setSelectedCourses([]);
                    setIsNewAssignment(false);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCourseMapping}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isProcessing && (
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  {isProcessing
                    ? 'Processing...'
                    : isNewAssignment
                    ? 'Assign'
                    : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Management Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-0 border w-full max-w-5xl shadow-2xl rounded-xl bg-white max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">
                  Role Management
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setRoleFormMode('create');
                  setSelectedRole(null);
                  setRoleFormData({
                    role_name: '',
                    description: '',
                    role_type: '',
                    category: '',
                    priority: 50,
                    is_active: true,
                  });
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    {roleFormMode === 'create' ? 'Create New Role' : 'Edit Role'}
                  </h4>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={roleFormData.role_name}
                        onChange={(e) => {
                          const newRoleName = e.target.value;
                          const roleTypeValue = newRoleName
                            .toLowerCase()
                            .replace(/\s+/g, '-');
                          setRoleFormData({
                            ...roleFormData,
                            role_name: newRoleName,
                            role_type: roleTypeValue,
                          });
                        }}
                        placeholder="e.g., Senior Lecturer"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={roleFormData.description}
                        onChange={(e) =>
                          setRoleFormData({
                            ...roleFormData,
                            description: e.target.value,
                          })
                        }
                        placeholder="Brief description of the role..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role Type{' '}
                        <span className="text-xs text-gray-500">
                          (Auto-generated from Role Name)
                        </span>
                      </label>
                      <input
                        type="text"
                        value={roleFormData.role_type}
                        readOnly
                        placeholder="Auto-generated..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This field is automatically generated from the role
                        name in lowercase
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        value={roleFormData.category}
                        onChange={(e) =>
                          setRoleFormData({
                            ...roleFormData,
                            category: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Select Category</option>
                        <option value="academic">Academic</option>
                        <option value="sales">Sales</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority (0-100)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={roleFormData.priority}
                        onChange={(e) =>
                          setRoleFormData({
                            ...roleFormData,
                            priority: parseInt(e.target.value, 10) || 0,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={roleFormData.is_active}
                        onChange={(e) =>
                          setRoleFormData({
                            ...roleFormData,
                            is_active: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <label
                        htmlFor="is_active"
                        className="text-sm font-medium text-gray-700"
                      >
                        Active
                      </label>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={handleSaveRole}
                        className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 font-medium"
                      >
                        {roleFormMode === 'create'
                          ? 'Create Role'
                          : 'Update Role'}
                      </button>
                      {roleFormMode === 'edit' && (
                        <button
                          onClick={() => {
                            setRoleFormMode('create');
                            setSelectedRole(null);
                            setRoleFormData({
                              role_name: '',
                              description: '',
                              role_type: '',
                              category: '',
                              priority: 50,
                              is_active: true,
                            });
                          }}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium"
                        >
                          Cancel Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    Existing Roles ({availableRoles.length})
                  </h4>

                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {availableRoles.map((role) => (
                      <div
                        key={role.id}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          selectedRole && selectedRole.id === role.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 bg-white hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h5 className="font-semibold text-gray-900">
                                {role.label}
                              </h5>
                              {role.is_active ? (
                                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                                  Inactive
                                </span>
                              )}
                            </div>
                            {role.description && (
                              <p className="text-sm text-gray-600 mt-1">
                                {role.description}
                              </p>
                            )}
                            <div className="flex gap-3 mt-2 text-xs">
                              <span className="text-gray-500">
                                Type:{' '}
                                <span className="font-medium text-gray-700">
                                  {role.type}
                                </span>
                              </span>
                              <span className="text-gray-500">
                                Priority:{' '}
                                <span className="font-medium text-gray-700">
                                  {role.priority}
                                </span>
                              </span>
                              {role.category && (
                                <span className="text-gray-500">
                                  Category:{' '}
                                  <span className="font-medium text-gray-700">
                                    {role.category}
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <button
                              onClick={() => handleEditRole(role)}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                              title="Edit role"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteRole(role)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                              title="Delete role"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setRoleFormMode('create');
                  setSelectedRole(null);
                  setRoleFormData({
                    role_name: '',
                    description: '',
                    role_type: '',
                    category: '',
                    priority: 50,
                    is_active: true,
                  });
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Staff Role Modal */}
      {showChangeRoleModal && staffForRoleChange && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <svg
                    className="w-6 h-6 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Change Role
                  </h3>
                  <p className="text-sm text-gray-500">
                    {staffForRoleChange.name}
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Role
                </label>
                <div
                  className={`px-4 py-2 rounded-lg border-2 ${getStaffTypeColor(
                    staffForRoleChange.type,
                  )} font-medium`}
                >
                  {staffForRoleChange.type || 'No Role'}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select New Role <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedNewRole}
                  onChange={(e) => setSelectedNewRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">-- Select a role --</option>
                  {availableRoles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowChangeRoleModal(false);
                    setStaffForRoleChange(null);
                    setSelectedNewRole('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStaffRoleChange}
                  disabled={!selectedNewRole}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Update Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Courses List Modal */}
      {showCoursesListModal && staffForCoursesList && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-0 border w-full max-w-2xl shadow-2xl rounded-xl bg-white max-h-[85vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Assigned Courses
                  </h3>
                  <p className="text-blue-100 text-sm">
                    {staffForCoursesList.name} •{' '}
                    {staffForCoursesList.assignedCoursesCount}{' '}
                    {staffForCoursesList.assignedCoursesCount === 1
                      ? 'Course'
                      : 'Courses'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowCoursesListModal(false);
                  setStaffForCoursesList(null);
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {staffForCoursesList.assignedCoursesCount > 0 ? (
                <div className="space-y-3">
                  {staffForCoursesList.assignedCourses.map((course, index) => (
                    <div
                      key={course.id}
                      className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200 hover:border-blue-300 transition-all"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-semibold text-sm">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-900 text-base">
                                  {course.courseName}
                                </h4>
                                {course.scheduleType && (
                                  <span
                                    className={`px-2 py-0.5 text-xs font-medium rounded ${
                                      course.scheduleType === 'weekday'
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-orange-100 text-orange-800'
                                    }`}
                                  >
                                    {course.scheduleType === 'weekday'
                                      ? 'Weekday'
                                      : 'Weekend'}
                                  </span>
                                )}
                              </div>
                              {course.courseShortName && (
                                <p className="text-sm text-gray-500 mt-1">
                                  Code: {course.courseShortName}
                                </p>
                              )}
                              <div className="flex gap-2 mt-2 flex-wrap">
                                {course.isView && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                    View
                                  </span>
                                )}
                                {course.isAdd && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                                    Add
                                  </span>
                                )}
                                {course.isUpdate && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                                    Update
                                  </span>
                                )}
                                {course.isDelete && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                                    Delete
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            handleRemoveCourseFromList(
                              staffForCoursesList,
                              course.id,
                              course.courseName || '',
                            )
                          }
                          disabled={removingCourseId === course.id}
                          className="flex items-center gap-2 px-3 py-2 text-red-600 hover:text-white hover:bg-red-600 rounded-lg border border-red-300 hover:border-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove course"
                        >
                          {removingCourseId === course.id ? (
                            <>
                              <svg
                                className="animate-spin h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              <span className="text-sm font-medium">Removing...</span>
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                              <span className="text-sm font-medium">Remove</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    No Courses Assigned
                  </h4>
                  <p className="text-gray-500">
                    This staff member has no courses assigned yet.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {staffForCoursesList.assignedCoursesCount > 0 ? (
                  <span>
                    Showing{' '}
                    <span className="font-semibold text-gray-900">
                      {staffForCoursesList.assignedCoursesCount}
                    </span>{' '}
                    assigned{' '}
                    {staffForCoursesList.assignedCoursesCount === 1
                      ? 'course'
                      : 'courses'}
                  </span>
                ) : (
                  <span>
                    Use "Assign Courses" button to add courses
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  setShowCoursesListModal(false);
                  setStaffForCoursesList(null);
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
